import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { CartService } from '../services/cart/cart.service';
import { ClientOrderService } from '../services';
import { camelCase, isArray, kebabCase } from 'lodash';
import { CommonModule } from '@angular/common';
import { PanelComponent } from '../panel/panel.component';
import { AlertComponent } from '../components/alert/alert.component';
import { TranslatePipe } from '@ngx-translate/core';
import { GoogleTagManagerService } from 'angular-google-tag-manager';

@Component({
  selector: 'webmodule-order-panel-cart',
  templateUrl: './panel-cart.component.html',
  styles: [
    `
      :host {
        display: block;
        position: sticky;
        top: 1rem;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, AlertComponent, PanelComponent, TranslatePipe],
  standalone: true,
})
export class PanelCartComponent {
  private readonly clientOrderService = inject(ClientOrderService);
  private readonly gtmService = inject(GoogleTagManagerService);
  protected readonly number = Number;
  protected readonly camelCase = camelCase;
  protected readonly isNaN = isNaN;
  readonly cartService = inject(CartService);
  readonly cartState = this.cartService.cartState;
  readonly canOrder = this.clientOrderService.canOrder;
  readonly canFinalizeOrder = this.clientOrderService.validOrderDetails;
  readonly pendingRequest = this.clientOrderService.pendingRequest;
  readonly uiState = this.clientOrderService.uiState;
  readonly internetOnly = computed(() => 'internet-only' === kebabCase(this.cartState()?.get('internet')?.type));
  readonly showAllTvOptions = signal<boolean>(false);
  totalOneTimeCost = signal<number>(0);
  totalRecurringCost = signal<number>(0);
  isCartOverview = input<boolean>(false);

  constructor() {
    effect(() => {
      const cartState = this.cartState();

      if (cartState) {
        const items = Array.from(cartState.values());
        const totals = this.calculateTotalPrices(items);

        this.totalOneTimeCost.set(totals.oneTime);
        this.totalRecurringCost.set(totals.monthly);
      }

      if (this.isCartOverview()) {
        this.showAllTvOptions.set(true);
      }
    });
  }

  addDetails(): void {
    this.uiState.set('DETAILS');
    this.pushTag({ event: 'continueToCheckout', page: 'configuration' });
  }

  editOrder(): void {
    this.uiState.set('CONFIG');
    this.pushTag({ event: 'continueConfigurationFromCheckout', page: 'checkout' });
  }

  completeOrder(): void {
    const totalRecurringCost = this.totalRecurringCost();
    this.pendingRequest.set(true);
    this.clientOrderService
      .submitOrder()
      .then((res) => (isArray(res) ? res[0] : res))
      .then((res) => {
        this.pushTag({ event: 'orderSubmitted', orderId: res.id, totalMonthlyRecurringCost: totalRecurringCost, page: 'checkout' });
        this.uiState.set('COMPLETED');
      })
      .catch((error) => {
        console.log(error);
      });
  }

  toggleTvOptions(): void {
    this.showAllTvOptions.update((res) => !res);
  }

  private calculateTotalPrices(items: any[]) {
    let oneTimeTotal = 0;
    let monthlyTotal = 0;
    items.forEach((item) => {
      if (isArray(item)) {
        const { oneTime, monthly } = this.calculateTotalPrices(item);
        oneTimeTotal += oneTime;
        monthlyTotal += monthly;
        return;
      }

      const itemValue = isNaN(item.value) ? 1 : Number(item.value);
      const itemPrice = Number(item.price) * itemValue;
      if (item.paymentFrequency) {
        if ('monthly' === kebabCase(item.paymentFrequency)) {
          monthlyTotal += itemPrice;
        } else {
          oneTimeTotal += itemPrice;
        }
      } else {
        monthlyTotal += itemPrice;
      }
    });

    const activationPrice = this.cartService.activationPrice();
    oneTimeTotal += Number(activationPrice) ?? 0;

    return { oneTime: oneTimeTotal, monthly: monthlyTotal };
  }

  private pushTag(gtmEvent: { event: string; [p: string]: any }) {
    console.log('pushTag', gtmEvent);
    this.gtmService.pushTag(gtmEvent).then(() => console.log('GTM', gtmEvent.event));
  }
}
